import PropTypes from 'prop-types';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import throttle from 'lodash/throttle';
import { setTripSearchInputs } from '../../../../../store/slices/tripSearch';
import { autocompleteRequest } from '../../../../../store/slices/autocomplete';
import { AutocompleteType } from '../../../../../constants';
import SearchAutocomplete from '../../../../SearchAutocomplete';

const toLabel = ({ country, region, regionName, town }) => {
  if (town) return `${town}, ${country}`;
  if (region) return `${regionName || region}, ${country}`;
  return country;
};

const TourDestinationSearch = ({ tripId }) => {
  const isTripSearch = !tripId;
  const dispatch = useDispatch();
  const { searchInputs } = useSelector((store) => store.tripSearch);
  const autocompletePropertyName = 'tourAutocompleteResults';
  const autocompleteResults = useSelector((store) => store.autocomplete[autocompletePropertyName]);

  const { country, region, regionName, town, text } = searchInputs.tour;
  const [selected, setSelected] = useState(null);
  const hasUserTypedRef = useRef(false);
  const typedTextRef = useRef(null);

  useEffect(() => {
    if (hasUserTypedRef.current) return;
    const label = text || (country && toLabel({ country, region, regionName, town }));
    setSelected(label ? { label, value: label } : null);
  }, [country, region, regionName, town, text]);

  const setSearchInputs = useCallback(
    (tour) => {
      dispatch(
        setTripSearchInputs({
          ...searchInputs,
          tour: {
            ...searchInputs.tour,
            country: undefined,
            region: undefined,
            regionName: undefined,
            town: undefined,
            text: undefined,
            ...tour,
          },
        }),
      );
    },
    [dispatch, searchInputs],
  );

  const searchByText = (typedText) => {
    const trimmed = (typedText || '').trim();
    if (!trimmed) {
      setSelected(null);
    }
    setSearchInputs(trimmed ? { text: trimmed } : {});
  };

  const handleChange = (value) => {
    hasUserTypedRef.current = true;
    typedTextRef.current = null;
    if (typeof value === 'string' || !value) {
      const typedText = (value || '').trim();
      // Enter over free text makes the autocomplete drop the typed text, so it is put back as a value
      setSelected(typedText ? { label: typedText, value: typedText } : null);
      searchByText(typedText);
      return;
    }
    setSelected(value);
    setSearchInputs({
      country: value.country,
      region: value.region,
      regionName: value.regionName,
      town: value.town,
    });
  };

  const commitTypedText = () => {
    if (typedTextRef.current === null) {
      return;
    }
    const typed = typedTextRef.current.trim();
    typedTextRef.current = null;
    if (typed === ((selected && selected.label) || '').trim()) {
      return;
    }
    setSelected(typed ? { label: typed, value: typed } : null);
    searchByText(typed);
  };

  // MUI marks a highlighted suggestion with aria-activedescendant on the input; Enter then belongs
  // to the autocomplete, otherwise it commits the typed text.
  const handleKeyDownCapture = (event) => {
    if (event.key !== 'Enter' || event.target.getAttribute('aria-activedescendant')) {
      return;
    }
    event.stopPropagation();
    commitTypedText();
  };

  // oxlint-disable-next-line react/exhaustive-deps
  const handleInputChange = useCallback(
    throttle((typed) => {
      hasUserTypedRef.current = true;
      typedTextRef.current = typed || '';
      dispatch(
        autocompleteRequest({
          autocompleteTypes: AutocompleteType.TOUR_DESTINATIONS,
          responseType: autocompletePropertyName,
          query: typed,
        }),
      );
    }, 50),
    [dispatch, autocompletePropertyName],
  );

  useEffect(() => {
    if (!autocompleteResults || !autocompleteResults.length) {
      dispatch(
        autocompleteRequest({
          autocompleteTypes: AutocompleteType.TOUR_DESTINATIONS,
          responseType: autocompletePropertyName,
        }),
      );
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  return (
    <div
      className={clsx({ 'w-56': !isTripSearch, 'w-full': isTripSearch })}
      onKeyDownCapture={handleKeyDownCapture}
    >
      <SearchAutocomplete
        placeholderText="Country, region, town, tour name or tour ID"
        onChange={(newValue) => handleChange(newValue)}
        options={autocompleteResults}
        values={selected}
        popperMinWidth={350}
        onInputChange={handleInputChange}
        onBlur={commitTypedText}
        variant={
          isTripSearch
            ? [
                SearchAutocomplete.Variants.outlineNoRightRadius,
                SearchAutocomplete.Variants.outlineNoBorderWidth,
              ]
            : undefined
        }
        disableClearable
        doUseThemeBackground
      />
    </div>
  );
};

TourDestinationSearch.propTypes = {
  tripId: PropTypes.string,
};

TourDestinationSearch.defaultProps = {
  tripId: undefined,
};

export default memo(TourDestinationSearch);
